/** Browser STEP preview. OpenCascade WASM runs in the page; no extra server process. */

export const STEP_CAD_PREVIEW_MAX_BYTES = 32 * 1024 * 1024;

const STEP_EXTENSIONS = new Set(["step", "stp"]);

export type StepCadTriangleMesh = {
  name: string;
  colorHex: number;
  positions: Float32Array;
  normals: Float32Array | null;
  indices: Uint32Array | null;
};

type OcctNumberArray = { array?: ArrayLike<number> | null };

export type OcctStepMeshLike = {
  name?: string | null;
  color?: ArrayLike<number> | null;
  attributes?: {
    position?: OcctNumberArray | null;
    normal?: OcctNumberArray | null;
  } | null;
  index?: OcctNumberArray | null;
};

export type OcctStepImportLike = {
  success?: boolean;
  meshes?: OcctStepMeshLike[] | null;
};

type OcctStepModule = {
  ReadStepFile: (content: Uint8Array, params: null) => OcctStepImportLike;
};

type OcctStepFactory = (options?: { locateFile?: (file: string) => string }) => Promise<OcctStepModule>;

function filenameExtension(filename: string): string | null {
  const match = /\.([^.]+)$/i.exec(filename.trim());
  return match ? match[1]!.toLowerCase() : null;
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function colorToHex(color: ArrayLike<number> | null | undefined): number {
  if (color == null || color.length < 3) return 0xb8c4ce;
  const red = Math.round(clampUnit(Number(color[0])) * 255);
  const green = Math.round(clampUnit(Number(color[1])) * 255);
  const blue = Math.round(clampUnit(Number(color[2])) * 255);
  return (red << 16) | (green << 8) | blue;
}

function copyNumbers(values: ArrayLike<number>, TypedArray: Float32ArrayConstructor): Float32Array;
function copyNumbers(values: ArrayLike<number>, TypedArray: Uint32ArrayConstructor): Uint32Array;
function copyNumbers(
  values: ArrayLike<number>,
  TypedArray: Float32ArrayConstructor | Uint32ArrayConstructor,
): Float32Array | Uint32Array {
  const copy = new TypedArray(values.length);
  for (let index = 0; index < values.length; index += 1) {
    copy[index] = Number(values[index]);
  }
  return copy;
}

export function isStepCadFilename(
  filename: string | null | undefined,
  contentType?: string | null,
): boolean {
  const extension = filenameExtension(filename ?? "");
  if (extension != null && STEP_EXTENSIONS.has(extension)) return true;
  const mime = (contentType ?? "").toLowerCase();
  return mime.includes("step") || mime === "application/stp" || mime === "model/step";
}

export function stepCadPreviewTooLarge(byteLength: number): boolean {
  return byteLength > STEP_CAD_PREVIEW_MAX_BYTES;
}

export function stepCadPreviewTooLargeMessage(): string {
  const limitMb = Math.floor(STEP_CAD_PREVIEW_MAX_BYTES / (1024 * 1024));
  return `This STEP file is too large to preview in the browser (${limitMb} MB limit). Use Download to open it locally.`;
}

export function meshesFromOcctStepImport(result: OcctStepImportLike): StepCadTriangleMesh[] {
  if (result.success !== true) {
    throw new Error("Could not read this STEP file. Use Download to open it locally.");
  }
  const meshes: StepCadTriangleMesh[] = [];
  for (const mesh of result.meshes ?? []) {
    const position = mesh.attributes?.position?.array;
    if (position == null || position.length < 9 || position.length % 3 !== 0) continue;
    const normals = mesh.attributes?.normal?.array;
    const indices = mesh.index?.array;
    const useNormals = normals != null && normals.length === position.length;
    const useIndices = indices != null && indices.length >= 3;
    meshes.push({
      name: mesh.name?.trim() || "Solid",
      colorHex: colorToHex(mesh.color),
      positions: copyNumbers(position, Float32Array),
      normals: useNormals ? copyNumbers(normals, Float32Array) : null,
      indices: useIndices ? copyNumbers(indices, Uint32Array) : null,
    });
  }
  if (meshes.length === 0) {
    throw new Error("This STEP file has no displayable surfaces. Use Download to open it locally.");
  }
  return meshes;
}

export async function importStepCadMeshes(bytes: Uint8Array): Promise<StepCadTriangleMesh[]> {
  if (stepCadPreviewTooLarge(bytes.byteLength)) {
    throw new Error(stepCadPreviewTooLargeMessage());
  }
  const imported = (await import("occt-import-js")) as { default?: OcctStepFactory };
  const factory = imported.default;
  if (typeof factory !== "function") {
    throw new Error("STEP preview library did not load. Use Download to open the file locally.");
  }
  const wasmModule = (await import("occt-import-js/dist/occt-import-js.wasm?url")) as { default: string };
  const occt = await factory({
    locateFile: (file) => (file.endsWith(".wasm") ? wasmModule.default : file),
  });
  return meshesFromOcctStepImport(occt.ReadStepFile(bytes, null));
}
